<template lang="pug">
    //- The questions of a registration template as form inputs, laid out in the template's
    //- order and widths. Custom answers live in the `value` object keyed by field id (a
    //- checkbox group holds an array); the fixed questions (salutation, name, email, phone,
    //- slot) are rendered by the surrounding form through named slots when `withFixed` is set.
    el-row.registration-field-inputs.no-padding(:gutter="20")
        el-col(v-for="field in renderedFields" :key="field.id" :lg="span(field)" :md="span(field)")
            //- A fixed field is drawn by the parent (it binds to the booker / the slot); the
            //- named slot gets the field so the parent can use its label and required flag.
            slot(v-if="field.key" :name="field.key" :field="field")
            el-form-item(v-else :class="{ 'is-required': field.required }" :label="field.label")
                template(v-if="field.type === 'textarea'")
                    el-input(type="textarea" :rows="3" :placeholder="field.placeholder || ''" :value="answer(field)" @input="update(field, $event)")
                template(v-else-if="field.type === 'number'")
                    el-input(type="number" :placeholder="field.placeholder || ''" :value="answer(field)" @input="update(field, $event)")
                template(v-else-if="field.type === 'date'")
                    el-date-picker.is-full-width(
                        type="date"
                        value-format="yyyy-MM-dd"
                        :picker-options="{ firstDayOfWeek: 1 }"
                        :format="$t('dd.MM.yyyy')"
                        :placeholder="field.placeholder || ''"
                        :value="answer(field)"
                        @input="update(field, $event)"
                    )
                template(v-else-if="field.type === 'time'")
                    el-time-picker.is-full-width(
                        value-format="HH:mm"
                        format="HH:mm"
                        :placeholder="field.placeholder || ''"
                        :value="answer(field)"
                        @input="update(field, $event)"
                    )
                template(v-else-if="field.type === 'select'")
                    el-select.is-full-width(clearable placeholder="" :value="answer(field)" @input="update(field, $event)")
                        el-option(v-for="option in field.options" :key="option.id" :label="option.label" :value="option.value")
                template(v-else-if="field.type === 'radio'")
                    el-radio-group(:value="answer(field)" @input="update(field, $event)")
                        el-radio(v-for="option in field.options" :key="option.id" :label="option.value") {{ option.label }}
                template(v-else-if="field.type === 'checkbox'")
                    el-checkbox(:value="answer(field) === '1'" @input="update(field, $event ? '1' : '')") {{ field.label }}
                template(v-else-if="field.type === 'checkbox_group'")
                    el-checkbox-group(:value="answer(field) || []" @input="update(field, $event)")
                        el-checkbox(v-for="option in field.options" :key="option.id" :label="option.value") {{ option.label }}
                template(v-else)
                    el-input(:placeholder="field.placeholder || ''" :value="answer(field)" @input="update(field, $event)")
                .help(v-if="field.help_text") {{ field.help_text }}
</template>

<i18n>
{
  "de": {
    "dd.MM.yyyy": "dd.MM.yyyy"
  }
}
</i18n>

<script>
import _ from 'lodash'

// Turns the stored rows of a booking ({ label, value, field: { id } }) into the answer object.
export const valuesToAnswers = (fields, values) => {
    const answers = {}
    const byId = _.keyBy(fields, 'id')
    ;(values || []).forEach(row => {
        const fieldId = _.get(row, 'field.id')
        const field = byId[fieldId]
        if (!field) return
        if (field.type === 'checkbox_group') {
            if (!answers[fieldId]) answers[fieldId] = []
            answers[fieldId].push(row.value)
        } else {
            answers[fieldId] = row.value
        }
    })
    return answers
}

// The fixed fields (salutation, name, email, phone, slot) answer through the booker and the
// slot; only the custom ones carry booking values.
const customFields = fields => (fields || []).filter(field => !field.key)

// The stored answers as display text per field id: several values (checkboxes) joined.
export const answersByField = values => {
    const byField = {}
    ;(values || []).forEach(row => {
        const fieldId = _.get(row, 'field.id')
        if (!fieldId || row.value === null || row.value === '') return
        byField[fieldId] = byField[fieldId] ? `${byField[fieldId]}, ${row.value}` : String(row.value)
    })
    return byField
}

// Turns the answer object into what EX_EVNT_BookingValueInput expects: one row per value.
export const answersToValues = (fields, answers) => {
    const values = []
    customFields(fields).forEach(field => {
        const answer = _.get(answers, field.id)
        const list = Array.isArray(answer) ? answer : [answer]
        list.forEach(value => {
            if (value === undefined || value === null || value === '') return
            values.push({ field: { id: field.id }, value: String(value) })
        })
    })
    return values
}

// The label of the first required question without an answer, or null.
export const findMissingRequiredAnswer = (fields, answers) => {
    const missing = customFields(fields).find(field => {
        if (!field.required) return false
        const answer = _.get(answers, field.id)
        if (Array.isArray(answer)) return !answer.length
        return answer === undefined || answer === null || answer === ''
    })
    return missing ? missing.label : null
}

export default {
    name: 'RegistrationFieldInputs',
    props: {
        // the template's fields
        fields: { type: Array, default: () => [] },
        // answers keyed by field id
        value: { type: Object, default: () => ({}) },
        // also lay out the fixed fields (through the named slots) in template order
        withFixed: { type: Boolean, default: false },
    },
    computed: {
        renderedFields() {
            return this.withFixed ? this.fields : customFields(this.fields)
        },
    },
    methods: {
        span(field) {
            switch (field.width) {
                case 'half':
                    return 12
                case 'third':
                    return 8
                default:
                    return 24
            }
        },
        answer(field) {
            return _.get(this.value, field.id)
        },
        update(field, answer) {
            this.$emit('input', { ...this.value, [field.id]: answer })
        },
    },
}
</script>

<style lang="sass" scoped>
    .help
        line-height: 1.4
</style>
